import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAllInsights } from '@/components/pages/Insights/useInsights';
import { selectBrainId } from '@/redux/session/selectors';
import { INSIGHT_TYPES } from '@/shared/types/insights';

import { BooleanInsightFilter } from './BooleanInsightFilter';
import { CategoricalInsightFilter } from './CategoricalInsightFilter';
import { DateTimeInsightFilter } from './DateTimeInsightFilter';
import { NumberInsightFilter } from './NumberInsightFilter';
import { StringInsightFilter } from './StringInsightFilter';

interface InsightFilterProps {
  filterIndex: number;
  insightId: string;
}

export const InsightFilter = ({ filterIndex, insightId }: InsightFilterProps) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { data: allInsights, isLoading, error } = useAllInsights(brainId);

  const insight = useMemo(() => {
    if (!allInsights) {
      return null;
    }
    return allInsights.find((i) => i.insight_id === insightId);
  }, [allInsights, insightId]);

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-foreground-muted">
        {t('analytics.filters.loading_insight')}
      </div>
    );
  }

  if (error || !insight) {
    return (
      <div className="p-4 text-sm text-destructive">
        {t('analytics.filters.failed_to_load_insight')}
      </div>
    );
  }

  // Render appropriate filter based on insight type
  switch (insight.type) {
    case INSIGHT_TYPES.BOOLEAN:
      return <BooleanInsightFilter filterIndex={filterIndex} insight={insight} />;

    case INSIGHT_TYPES.STRING:
    case INSIGHT_TYPES.EMAIL:
    case INSIGHT_TYPES.PHONE_NUMBER:
      return <StringInsightFilter filterIndex={filterIndex} insight={insight} />;

    case INSIGHT_TYPES.CATEGORICAL:
      return <CategoricalInsightFilter filterIndex={filterIndex} insight={insight} />;

    case INSIGHT_TYPES.INTEGER:
    case INSIGHT_TYPES.NUMBER:
      return <NumberInsightFilter filterIndex={filterIndex} insight={insight} />;

    case INSIGHT_TYPES.DATETIME:
      return <DateTimeInsightFilter filterIndex={filterIndex} insight={insight} />;

    default:
      return (
        <div className="p-4 text-sm text-foreground-muted">
          {t('analytics.filters.unsupported_insight_type')}
        </div>
      );
  }
};
